/**
 * Cliente do Modelo B: ASR + GPT + TTS em componentes separados.
 *
 * O WebRTC permanece — mas a sessão negociada com a OpenAI é só de
 * transcrição. A conversa fecha o ciclo em três pernas:
 *
 *   1. Microfone → sessão de transcrição da Realtime (WebRTC): cada turno de
 *      fala chega como eventos de transcrição pelo data channel.
 *   2. Turno completo → `POST /api/pipeline/calls/{id}/respond`: o backend
 *      roda o Chat Completions com o prompt da política e executa as
 *      ferramentas sob a mesma governança do Modelo A.
 *   3. Texto da Alice → `POST /api/pipeline/tts` → toca aqui via WebAudio.
 *
 * O contrato com a tela é o MESMO do Modelo A (`RealtimeHandle` + callbacks):
 * trocar de modelo não reescreve painel nenhum.
 */

import { api } from './api'
import type { RealtimeCallbacks, RealtimeHandle } from './realtime'
import type { Artifact, ToolExecutionResponse } from './types'

export interface PipelineCallbacks extends Omit<RealtimeCallbacks, 'onToolCall'> {
  /**
   * Artefato executado no servidor durante o turno. No Modelo A a tela executa
   * a ferramenta e publica; aqui ela só publica — a execução já aconteceu.
   * `tool` é o nome da ferramenta: a tela reage a algumas (ex.: o hand-off
   * para o WhatsApp vira botão de continuação).
   *
   * Latência não passa por aqui de propósito: o backend a registra e a tela
   * /debug a exibe — número de delay não aparece nas telas de demonstração.
   */
  onArtifact(artifact: Artifact, timeline: ToolExecutionResponse['timeline'], tool: string): void
}

interface TranscriptionEvent {
  type: string
  delta?: string
  transcript?: string
  error?: { message?: string }
  [key: string]: unknown
}

/** O endpoint de TTS emite PCM s16le mono nesta taxa (fixa da API). */
const SAMPLE_RATE = 24000

export async function connectPipeline(
  callbacks: PipelineCallbacks,
  opts: { callId: string; at(): string },
): Promise<RealtimeHandle> {
  const session = await api.pipelineSession()

  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  })

  // Microfone da sala — única mídia que trafega no WebRTC neste modelo.
  const mic = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  })
  for (const track of mic.getTracks()) pc.addTrack(track, mic)

  // A voz sintetizada toca por WebAudio. O MediaStreamDestination entrega ao
  // orb o mesmo tipo de MediaStream que o Modelo A recebe da Realtime.
  const AudioCtor = window.AudioContext ?? (window as any).webkitAudioContext
  const context: AudioContext = new AudioCtor()
  const speakers = context.createMediaStreamDestination()
  callbacks.onRemoteStream(speakers.stream)

  let closed = false
  let paused = false
  // A fala chega em PCM streaming e começa a tocar nos primeiros bytes; estes
  // dois controles são o que o barge-in e a pausa usam para cortá-la.
  let interruptPlayback: (() => void) | null = null
  let speaking = false

  const stopPlayback = () => {
    interruptPlayback?.()
    interruptPlayback = null
  }

  /**
   * Toca o stream PCM (24 kHz, s16le, mono) do backend enquanto ele chega.
   *
   * Cada chunk vira um AudioBuffer agendado em sequência no relógio do
   * AudioContext — o primeiro áudio sai com o primeiro chunk da rede, não
   * depois do arquivo completo (docs/Materia_..._Latencia_2026.md §6.2).
   */
  const playPcmStream = async (
    response: Response,
    afterState: 'idle' | 'think',
  ): Promise<void> => {
    const reader = response.body?.getReader()
    if (!reader) throw new Error('Navegador sem suporte a streaming de áudio')

    let interrupted = false
    let finishWait: (() => void) | null = null
    const sources = new Set<AudioBufferSourceNode>()
    interruptPlayback = () => {
      interrupted = true
      void reader.cancel().catch(() => undefined)
      for (const source of sources) {
        try {
          source.stop()
        } catch {
          /* já parado */
        }
      }
      sources.clear()
      finishWait?.()
    }

    let leftover = new Uint8Array(0)
    let playhead = 0
    let started = false

    for (;;) {
      const { done, value } = await reader.read()
      if (done || interrupted || closed) break
      if (!value || value.length === 0) continue

      const merged = new Uint8Array(leftover.length + value.length)
      merged.set(leftover)
      merged.set(value, leftover.length)
      // s16le: cada amostra são 2 bytes — o byte ímpar sobra para o próximo chunk.
      const usable = merged.length - (merged.length % 2)
      leftover = merged.slice(usable)
      if (usable === 0) continue

      const samples = new Int16Array(merged.buffer, 0, usable / 2)
      const floats = new Float32Array(samples.length)
      for (let i = 0; i < samples.length; i += 1) floats[i] = samples[i] / 32768
      const buffer = context.createBuffer(1, floats.length, SAMPLE_RATE)
      buffer.copyToChannel(floats, 0)

      const source = context.createBufferSource()
      source.buffer = buffer
      source.connect(context.destination)
      source.connect(speakers)
      if (!started) {
        started = true
        speaking = true
        playhead = context.currentTime + 0.08 // colchão contra jitter da rede
        callbacks.onSpeakingChange('ai')
      }
      // Rede atrasou além do colchão: retoma dali, sem tentar "alcançar".
      if (playhead < context.currentTime) playhead = context.currentTime + 0.02
      source.start(playhead)
      playhead += buffer.duration
      sources.add(source)
      source.onended = () => sources.delete(source)
    }

    // A rede termina antes do som: espera o áudio agendado acabar de tocar.
    if (started && !interrupted) {
      const remainingMs = Math.max(0, (playhead - context.currentTime) * 1000) + 60
      await new Promise<void>((resolve) => {
        finishWait = resolve
        window.setTimeout(resolve, remainingMs)
      })
      finishWait = null
    }

    speaking = false
    interruptPlayback = null
    if (!closed && !interrupted) callbacks.onSpeakingChange(afterState)
  }

  // Conectores de espera ("entendo", "deixa eu ver aqui…") foram testados e
  // desativados: mascaravam a latência, mas soavam artificiais. O backend
  // mantém GET /api/pipeline/fillers para uma futura reativação.

  // Um turno por vez, na ordem em que fecharam. A fila também serializa o
  // sussurro do supervisor com os turnos da cliente.
  let queue: Promise<void> = Promise.resolve()
  const runTurn = (text: string, role: 'user' | 'system') => {
    queue = queue.then(async () => {
      if (closed || paused || !text.trim()) return
      try {
        callbacks.onSpeakingChange('think')
        const turn = await api.pipelineRespond(opts.callId, text, role, opts.at())
        if (closed) return

        for (const tool of turn.executed_tools) {
          if (tool.ok && tool.artifact) {
            callbacks.onArtifact(tool.artifact, tool.timeline, tool.name)
          }
        }

        if (!turn.text) return
        callbacks.onTranscriptDelta('ai', turn.text)
        callbacks.onTurnDone('ai')
        const speech = await api.pipelineTtsStream(turn.text, opts.callId)
        await playPcmStream(speech, 'idle')
      } catch (error) {
        callbacks.onError(
          error instanceof Error ? error : new Error('Pipeline falhou ao responder'),
        )
      }
    })
  }

  const channel = pc.createDataChannel('oai-events')
  channel.addEventListener('message', (event) => {
    let parsed: TranscriptionEvent
    try {
      parsed = JSON.parse(event.data as string)
    } catch {
      return
    }

    switch (parsed.type) {
      case 'conversation.item.input_audio_transcription.delta':
        callbacks.onSpeakingChange('cli')
        callbacks.onTranscriptDelta('cli', parsed.delta ?? '')
        break

      case 'conversation.item.input_audio_transcription.completed':
        callbacks.onTurnDone('cli')
        callbacks.onSpeakingChange('think')
        runTurn(parsed.transcript ?? '', 'user')
        break

      case 'input_audio_buffer.speech_started':
        // Barge-in do pipeline: quem corta a fala é este cliente, não a
        // OpenAI — a sessão de transcrição não tem resposta a interromper.
        if (speaking) callbacks.onAudit('Barge-in: cliente interrompeu · IA parou de falar')
        stopPlayback()
        callbacks.onSpeakingChange('cli')
        break

      case 'error':
        callbacks.onError(new Error(parsed.error?.message ?? 'Erro da sessão de transcrição'))
        break

      default:
        break
    }
  })
  channel.addEventListener('close', () => callbacks.onClose())

  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'failed') callbacks.onError(new Error('Conexão WebRTC falhou'))
    if (pc.connectionState === 'closed') callbacks.onClose()
  })

  // Troca de SDP — idêntica ao Modelo A, com o segredo da sessão de transcrição.
  const offer = await pc.createOffer()
  await pc.setLocalDescription(offer)

  const answer = await fetch(session.calls_url, {
    method: 'POST',
    body: offer.sdp,
    headers: {
      Authorization: `Bearer ${session.client_secret}`,
      'Content-Type': 'application/sdp',
    },
  })

  if (!answer.ok) {
    pc.close()
    for (const track of mic.getTracks()) track.stop()
    void context.close()
    throw new Error(
      `Sessão de transcrição recusou o SDP (${answer.status}): ${(await answer.text()).slice(0, 200)}`,
    )
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() })
  callbacks.onSpeakingChange('idle')

  return {
    model: `${session.asr_model} → ${session.chat_model} → ${session.tts_model}`,
    voice: session.voice,
    close() {
      closed = true
      stopPlayback()
      try {
        channel.close()
      } catch {
        /* já fechado */
      }
      for (const track of mic.getTracks()) track.stop()
      pc.close()
      void context.close()
    },
    whisper(text: string) {
      // Mesmo efeito do Modelo A: orientação entra como system e gera resposta.
      runTurn(`Orientação do supervisor: ${text}`, 'system')
    },
    setMuted(muted: boolean) {
      for (const track of mic.getAudioTracks()) track.enabled = !muted
    },
    setAgentPaused(next: boolean) {
      paused = next
      if (next) {
        stopPlayback()
        callbacks.onSpeakingChange('idle')
      }
      // Ao devolver o controle, nada a religar: a transcrição nunca parou e o
      // próximo turno completo volta a gerar resposta.
    },
  }
}
